import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart3, TrendingUp, TrendingDown } from 'lucide-react'
import { ReportsHeader, StatsCards, SpendingByCategory, CategoryChart, TransfersChart, ExportModal, type StatCard, type ExportFormat } from '@/components/reports'
import { reportsApi } from '@/services/api'

export function ReportsPage() {
  const { t } = useTranslation()
  const [selectedPeriod, setSelectedPeriod] = useState('month')
  const [stats, setStats] = useState<StatCard[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showExportModal, setShowExportModal] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const periods = [
    { id: 'week', label: t('reports.week') },
    { id: 'month', label: t('reports.month') },
    { id: 'year', label: t('reports.year') }
  ]

  useEffect(() => {
    loadReportsData()
  }, [selectedPeriod])

  const loadReportsData = async () => {
    setIsLoading(true)
    try {
      // Fetch reports data from API
      const response = await reportsApi.getSpendingSummary({
        period: selectedPeriod
      })
      
      // Extract data from API response
      const spendingData = response.data.data || response.data
      
      // Transform API data to stats format
      const statsData: StatCard[] = [
        {
          title: t('reports.totalSpent'),
          value: spendingData.total_spent || '0.00',
          currency: 'STN',
          change: spendingData.total_spent_change || '+0%',
          trend: (spendingData.total_spent_change || '').startsWith('-') ? 'down' : 'up',
          icon: TrendingUp
        },
        {
          title: t('reports.transactions'),
          value: String(spendingData.transaction_count || 0),
          change: spendingData.transaction_count_change || '+0%',
          trend: (spendingData.transaction_count_change || '').startsWith('-') ? 'down' : 'up',
          icon: BarChart3
        },
        {
          title: t('reports.savings'),
          value: spendingData.savings || '0.00',
          currency: 'STN',
          change: spendingData.savings_change || '+0%',
          trend: (spendingData.savings_change || '').startsWith('-') ? 'down' : 'up',
          icon: TrendingDown
        }
      ]
      
      setStats(statsData)
    } catch (error) {
      console.error('Error loading reports data:', error)
      // Fallback to default stats if API fails
      setStats([
        {
          title: t('reports.totalSpent'),
          value: '2,450.00',
          currency: 'STN',
          change: '+12.5%',
          trend: 'up',
          icon: TrendingUp
        },
        {
          title: t('reports.transactions'),
          value: '24',
          change: '+8.3%',
          trend: 'up',
          icon: BarChart3
        },
        {
          title: t('reports.savings'),
          value: '150.00',
          currency: 'STN',
          change: '-5.2%',
          trend: 'down',
          icon: TrendingDown
        }
      ])
    } finally {
      setIsLoading(false)
    }
  }

  const handleExport = () => {
    setShowExportModal(true)
  }

  const handleExportFormat = async (format: ExportFormat) => {
    setIsExporting(true)
    try {
      await reportsApi.exportReport({
        format,
        period: selectedPeriod
      })
      
      // Close modal after successful export
      setShowExportModal(false)
    } catch (error) {
      console.error('Error exporting report:', error)
      alert(t('reports.exportError', 'Erro ao exportar relatório'))
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background-light">
      <ReportsHeader
        selectedPeriod={selectedPeriod}
        periods={periods}
        onPeriodChange={setSelectedPeriod}
        onExport={handleExport}
      />

      <div className="p-4">
        {!isLoading && <StatsCards stats={stats} />}
        <SpendingByCategory period={selectedPeriod} />
        <CategoryChart period={selectedPeriod} />
        <TransfersChart period={selectedPeriod} />
      </div>

      <ExportModal
        isOpen={showExportModal}
        period={selectedPeriod}
        onClose={() => setShowExportModal(false)}
        onExport={handleExportFormat}
        isExporting={isExporting}
      />
    </div>
  )
}




